// Voice-note waveforms (M60, D221): 48 peaks computed on the sending device
// by decoding the recording with Web Audio, so every receiver draws the
// waveform at once from the sealed envelope (meta.waveform), without
// downloading or decoding anything. The levels captured while recording
// (#1) are the fallback when the browser can't decode its own recording.
import { FILE_LIMITS } from '@twinden/shared';

/**
 * The loudest sample in each of `bars` equal slices, scaled so the loudest
 * slice is 100. Silence stays 0 (the waveform draws a minimum height).
 */
export function peaksFrom(
  samples: ArrayLike<number>,
  bars: number = FILE_LIMITS.VOICE_WAVEFORM_BARS
): number[] {
  if (samples.length === 0 || bars <= 0) return [];
  const peaks = Array.from({ length: bars }, (_, bar) => {
    const start = Math.floor((bar * samples.length) / bars);
    const end = Math.max(
      start + 1,
      Math.floor(((bar + 1) * samples.length) / bars)
    );
    let peak = 0;
    for (let i = start; i < end && i < samples.length; i += 1) {
      const value = Math.abs(samples[i] ?? 0);
      if (value > peak) peak = value;
    }
    return peak;
  });
  const loudest = Math.max(...peaks);
  if (loudest === 0) return peaks.map(() => 0);
  return peaks.map((peak) => Math.round((peak / loudest) * 100));
}

type AudioContextLike = {
  decodeAudioData: (data: ArrayBuffer) => Promise<AudioBuffer>;
  close?: () => Promise<void>;
};

/**
 * Decodes a recording and returns its peaks, or null when this browser
 * can't decode it (the caller keeps the recorded levels).
 */
export async function computePeaks(
  blob: Blob,
  bars: number = FILE_LIMITS.VOICE_WAVEFORM_BARS
): Promise<number[] | null> {
  const Context =
    (
      globalThis as {
        AudioContext?: new () => AudioContextLike;
        webkitAudioContext?: new () => AudioContextLike;
      }
    ).AudioContext ??
    (globalThis as { webkitAudioContext?: new () => AudioContextLike })
      .webkitAudioContext;
  if (!Context) return null;
  let context: AudioContextLike | null = null;
  try {
    context = new Context();
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    const channel = buffer.getChannelData(0);
    const peaks = peaksFrom(channel, bars);
    return peaks.length === bars ? peaks : null;
  } catch {
    return null;
  } finally {
    void context?.close?.().catch(() => undefined);
  }
}

/** Playback speeds the chip cycles through (D221). */
export const VOICE_SPEEDS = [1, 1.5, 2] as const;
export type VoiceSpeed = (typeof VOICE_SPEEDS)[number];

export function nextSpeed(speed: number): VoiceSpeed {
  const index = VOICE_SPEEDS.indexOf(speed as VoiceSpeed);
  return VOICE_SPEEDS[(index + 1) % VOICE_SPEEDS.length]!;
}

/** "1×", "1.5×", "2×". */
export function speedLabel(speed: number): string {
  return `${speed}×`;
}

/** Where a pointer at `clientX` lands on a bar, 0–1 (drag to scrub, D221). */
export function fractionAt(
  clientX: number,
  rect: { left: number; width: number }
): number {
  if (rect.width <= 0) return 0;
  return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
}

// Where each voice note was left (D221), so scrolling away and back, or the
// bubble being redrawn, doesn't send it back to the start. For this session.
const positions = new Map<string, number>();

export function rememberPosition(id: string, seconds: number) {
  if (seconds > 0) positions.set(id, seconds);
  else positions.delete(id);
}

export function rememberedPosition(id: string): number {
  return positions.get(id) ?? 0;
}
